import React, { useContext, useEffect, useState } from 'react';
import moment from 'moment';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import Button from 'src/components/controls/forms/redesign/FormButton';
import RightOfWay, { RightOfWayHold } from 'src/models/contract_types/RightOfWay';
import { useFetch } from 'use-http';
import { push, replace } from 'connected-react-router';
import { IAppContextState } from 'src/reducers/AppContext';
import { AppContext } from 'src/providers/AppContextProvider';
import RHFLegend from 'src/components/controls/tempRHFControls/RHFLegend';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import RHFSelect from 'src/components/controls/tempRHFControls/RHFSelect';
import IndexReferenceType from 'src/models/IndexReferenceType';

const RightOfWayInfo = () => {
    const appContext = useContext<IAppContextState>(AppContext);
    const { register, handleSubmit, reset, errors, formState, setValue, watch } = useForm();
    const { get, put, post, response } = useFetch();
    const dispatch = useDispatch();
    const watchExpirationCalcFields = watch(['rightOfWayHolds[0].effectiveDate', 'rightOfWayHolds[0].primaryTerm', 'rightOfWayHolds[0].primaryTermTypeId']);
    const watchExtensionExpCalcFields = watch(['rightOfWayHolds[0].effectiveDate', 'rightOfWayHolds[0].extensionTerm', 'rightOfWayHolds[0].extensionTermTypeId']);

    const [rightOfWayData, setRightOfWayData] = useState<RightOfWay>(undefined);
    const [measurementTypes, setMeasurementTypes] = useState<Array<IndexReferenceType>>([]);

    const { isDirty } = formState;

    useEffect(() => {
        // TODO: Update this with ROW Id
        console.log('ROW Page loaded');
        getMeasurementTypes();
        if (appContext.rowId > 0) {
            getRowData(appContext.rowId);
        }
    }, []);

    useEffect(() => {
        reset(rightOfWayData);
    }, [rightOfWayData]);

    const getRowData = async (rightOfWayId: number) => {
        const result: RightOfWay = await get(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightOfWay/${rightOfWayId}`);
        if (response.ok) {
            setRightOfWayData(finalizeRightOfWay(result));
        } else {
            window.alert('Get Row Data failed');
        }
    };

    const getMeasurementTypes = async () => {
        const result = await get(`/v1.0/referenceTypes`);

        if (response.ok) {
            setMeasurementTypes(result);
        }
    };

    const updateRightOfWay = async (data: RightOfWay) => {
        await put(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightofway`, data);

        if (response.ok) {
            dispatch(
                push(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/row/${data.id}/rowhold/1`
                )
            );

            getRowData(appContext.rowId);
        } else {
            window.alert('Update ROW failed');
        }
    };

    const createRightOfWay = async (data: RightOfWay) => {
        const result = await post(`/v1.0/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/ownership/${appContext.ownershipId}/rightofway`, data);
        if (response.ok) {
            dispatch(
                replace(
                    `/clients/${appContext.clientNumber}/projects/${appContext.projectNumber}/sections/${appContext.sectionNumber}/surface-tracts/${appContext.surfaceTractNumber}/ownership/${appContext.ownershipId}/row/${result}/rowhold/1`
                )
            );
            getRowData(result);
        } else {
            window.alert('Create ROW failed');
        }
    };

    const onSubmit = (data: RightOfWay) => {
        data = finalizeRightOfWay(data);
        // data.id = leaseData.id;
        if (data.id > 0) {
            updateRightOfWay(data);
        } else {
            createRightOfWay(data);
        }
    };

    const finalizeRightOfWay = (data: RightOfWay) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.instrumentNumber = data.instrumentNumber ? parseInt(data.instrumentNumber + '', 10) : null;
        data.rowLengthTypeId = !isNaN(data.rowLengthTypeId) ? parseInt(data.rowLengthTypeId + '', 10) : null;
        data.rowWidthTypeId = !isNaN(data.rowWidthTypeId) ? parseInt(data.rowWidthTypeId + '', 10) : null;
        data.tempWidthTypeId = !isNaN(data.tempWidthTypeId) ? parseInt(data.tempWidthTypeId + '', 10) : null;
        data.pipeLineSizeTypeId = !isNaN(data.pipeLineSizeTypeId) ? parseInt(data.pipeLineSizeTypeId + '', 10) : null;
        data.notExceedingTypeId = !isNaN(data.notExceedingTypeId) ? parseInt(data.notExceedingTypeId + '', 10) : null;
        data.considerationTypeId = !isNaN(data.considerationTypeId) ? parseInt(data.considerationTypeId + '', 10) : null;
        data.damagesTypeId = !isNaN(data.damagesTypeId) ? parseInt(data.damagesTypeId + '', 10) : null;
        data.recordTypeId = !isNaN(data.recordTypeId) ? parseInt(data.recordTypeId + '', 10) : null;
        data.numberOfLines = data.numberOfLines ? parseInt(data.numberOfLines + '', 10) : null;

        data.rowNumber = data.rowNumber ? parseInt(data.rowNumber + '', 10) : null;
        data.book = data.book ? parseInt(data.book + '', 10) : null;
        data.page = data.page ? parseInt(data.page + '', 10) : null;

        data.rowLength = data.rowLength ? parseFloat(data.rowLength + '') : null;
        data.rowWidth = data.rowWidth ? parseFloat(data.rowWidth + '') : null;
        data.tempWidth = data.tempWidth ? parseFloat(data.tempWidth + '') : null;
        data.pipeLineSize = data.pipeLineSize ? parseFloat(data.pipeLineSize + '') : null;
        data.pipeLineNotExceeding = data.pipeLineNotExceeding ? parseFloat(data.pipeLineNotExceeding + '') : null;
        data.consideration = data.consideration ? parseFloat(data.consideration + '') : null;
        data.damages = data.damages ? parseFloat(data.damages + '') : null;
        data.payTermOrder = data.payTermOrder ? parseFloat(data.payTermOrder + '') : null;

        data.filedByMemo = data.filedByMemo ? data.filedByMemo : null;

        data.name = data.name ? data.name : '';
        data.originalLessee = data.originalLessee ? data.originalLessee : '';
        data.originalLessor = data.originalLessor ? data.originalLessor : '';
        data.lessorAka = data.lessorAka ? data.lessorAka : '';
        data.legalDescription = data.legalDescription ? data.legalDescription : '';
        
        data.createdDate = data.createdDate
            ? (moment(data.createdDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.leaseDate = data.leaseDate
            ? (moment(data.leaseDate).format('YYYY-MM-DD') as unknown as Date)
            : (moment().format('YYYY-MM-DD') as unknown as Date);
        data.effectiveDate = data.effectiveDate
            ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.recordDate = data.recordDate
            ? (moment(data.recordDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        if (data.rightOfWayHolds?.length > 0) {
            let formatted = new Array<RightOfWayHold>();
            data.rightOfWayHolds.forEach((rowhold: RightOfWayHold) => {
                formatted.push(finalizeRowhold(rowhold));
            });
            data.rightOfWayHolds = [...formatted];
        }

        return data;
    };

    const finalizeRowhold = (data: RightOfWayHold) => {
        data.id = data.id ? parseInt(data.id + '', 10) : 0;
        data.rightOfWayId = data.rightOfWayId ? parseInt(data.rightOfWayId + '', 10) : null;
        data.rowHoldNumber = data.rowHoldNumber ? parseInt(data.rowHoldNumber + '', 10) : null;
        data.primaryTerm = data.primaryTerm ? parseInt(data.primaryTerm + '', 10) : null;
        data.primaryTermTypeId = !isNaN(data.primaryTermTypeId) ? parseInt(data.primaryTermTypeId + '', 10) : null;
        data.royalty = data.royalty ? parseFloat(data.royalty + '') : null;
        data.royaltyTypeId = !isNaN(data.royaltyTypeId) ? parseInt(data.royaltyTypeId + '', 10) : null;
        data.extensionTerm = data.extensionTerm ? parseInt(data.extensionTerm + '', 10) : null;
        data.extensionTermTypeId = !isNaN(data.extensionTermTypeId) ? parseInt(data.extensionTermTypeId + '', 10) : null;
        data.agentId = !isNaN(data.agentId) ? parseInt(data.agentId + '', 10) : null;

        data.agreementType = data.agreementType ? data.agreementType : '';
        data.notes = data.notes ? data.notes : '';

        data.agreementDate = data.agreementDate
            ? (moment(data.agreementDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.effectiveDate = data.effectiveDate
            ? (moment(data.effectiveDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.expirationDate = data.expirationDate
            ? (moment(data.expirationDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.extensionExpirationDate = data.extensionExpirationDate
            ? (moment(data.extensionExpirationDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.voidDate = data.voidDate
            ? (moment(data.voidDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.commitDate = data.commitDate
            ? (moment(data.commitDate).format('YYYY-MM-DD') as unknown as Date)
            : null;
        data.inHandDate = data.inHandDate
            ? (moment(data.inHandDate).format('YYYY-MM-DD') as unknown as Date)
            : null;

        return data;
    };

    const getOptions = (type: string, item: string = '') => {
        if (measurementTypes?.length > 0) {
            return measurementTypes.filter(mt => mt.description === type).map(option => {
                if (type === 'Fee' && option.name === 'Per Item') {
                    return { value: option.id, label: `Per ${item}` };
                }
                return { value: option.id, label: option.name };
            });
        }
        return [];
    };

    const handleExpirationChange = () => {
        calcExpirationDate();
    };

    const handleExtensionExpirationChange = () => {
        calcExtensionExpirationDate();
    };

    const calcExpirationDate = () => {
        const term = watchExtensionExpCalcFields['rightOfWayHolds[0].primaryTerm']
            ? parseInt(watchExtensionExpCalcFields['rightOfWayHolds[0].primaryTerm'], 10)
            : undefined;
        const termUnit = watchExtensionExpCalcFields['rightOfWayHolds[0].primaryTermTypeId']
            ? watchExtensionExpCalcFields['rightOfWayHolds[0].primaryTermTypeId']
            : undefined;
        const effectiveDate = watchExtensionExpCalcFields['rightOfWayHolds[0].effectiveDate']
            ? new Date((watchExtensionExpCalcFields['rightOfWayHolds[0].effectiveDate']).toString()) || undefined
            : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
            let termString = measurementTypes.find((mt) => mt.id === parseInt(termUnit, 10))?.name;
            switch (termString) {
                case 'Years':
                    setValue(
                        'rightOfWayHolds[0].expirationDate',
                        new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'Months':
                    setValue(
                        'rightOfWayHolds[0].expirationDate',
                        new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'Days':
                    setValue('rightOfWayHolds[0].expirationDate', new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]);
                    break;

                default:
                    break;
            }
        } else {
            setValue('rightOfWayHolds[0].expirationDate', '');
        }
    };

    const calcExtensionExpirationDate = () => {
        const term = watchExpirationCalcFields['rightOfWayHolds[0].extensionTerm']
            ? parseInt(watchExpirationCalcFields['rightOfWayHolds[0].extensionTerm'], 10)
            : undefined;
        const termUnit = watchExpirationCalcFields['rightOfWayHolds[0].extensionTermTypeId']
            ? watchExpirationCalcFields['rightOfWayHolds[0].extensionTermTypeId']
            : undefined;
        const effectiveDate = watchExpirationCalcFields['rightOfWayHolds[0].effectiveDate']
            ? new Date((watchExpirationCalcFields['rightOfWayHolds[0].effectiveDate']).toString()) || undefined
            : undefined;

        if (term && termUnit && effectiveDate && !isNaN(term) && termUnit && effectiveDate instanceof Date) {
            switch (termUnit) {
                case 'years':
                    setValue(
                        'rightOfWayHolds[0].expirationDate',
                        new Date(effectiveDate.setFullYear(effectiveDate.getFullYear() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'months':
                    setValue(
                        'rightOfWayHolds[0].expirationDate',
                        new Date(effectiveDate.setMonth(effectiveDate.getMonth() + term)).toISOString().split('T')[0]
                    );
                    break;

                case 'days':
                    setValue('rightOfWayHolds[0].expirationDate', new Date(effectiveDate.setDate(effectiveDate.getDate() + term)).toISOString().split('T')[0]);
                    break;

                default:
                    break;
            }
        } else {
            setValue('rightOfWayHolds[0].expirationDate', '');
        }
    };

    return (
        <div className="lease-info-page">
            <div>
                <form onSubmit={handleSubmit(onSubmit)}>
                    <div>
                        {isDirty && (
                            <>
                                <div
                                    id="dirtyFlag"
                                    style={{
                                        position: 'fixed',
                                        top: '4.7rem',
                                        left: '0',
                                        width: '100%',
                                        color: 'white',
                                        backgroundColor: 'rgba(255,0,0,.5)',
                                        zIndex: 9000,
                                        padding: '1rem 0',
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <div>Unsaved Changes - Please Save Before Leaving Form</div>
                                    <div style={{ position: 'absolute', right: '.5rem', top: '50%', transform: 'translateY(-50%)', display: 'flex' }}>
                                        <div>
                                            <Button text="Save" type="submit" size="small" intent="primary" />
                                        </div>
                                        <div style={{ margin: '0 1rem', borderRight: '1px solid white', width: '1px' }} />
                                        <Button text="Cancel Changes" size="small" onClick={() => reset()} />
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <RHFLegend title="Right Of Way" />
                    <div hidden={true}>
                        <RHFInputBox
                            label=""
                            name="id"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>
                    <div className="form-row" style={{ width: '60%' }}>
                        <RHFInputBox
                            label="ROW Name"
                            name="name"
                            style={{ width: '100%'}}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="ROW Number"
                            name="rowNumber"
                            style={{ width: '100%'}}
                            register={register}
                            errors={errors}
                            disabled={true}
                        />
                    </div>
                    <div className="form-row">
                        <RHFInputBox
                            label="ROW Length"
                            name="rowLength"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="rowLengthTypeId"
                            label=""
                            options={getOptions('Length')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="ROW Width"
                            name="rowWidth"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="rowWidthTypeId"
                            label=""
                            options={getOptions('Length')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Temp Width"
                            name="tempWidth"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="tempWidthTypeId"
                            label=""
                            options={getOptions('Length')}
                            register={register}
                            errors={errors}
                        />
                        {/* <RHFInputBox label="Expiration Date" name="leasehold.expirationDate" register={register} type="date" errors={errors} />
                        <RHFInputBox
                            label="Royalty"
                            name="leasehold.royalty"
                            register={register}
                            onChange={(e) => setRoyalty(parseFloat(e.currentTarget.value))}
                            type="text"
                            rules={{ required: 'field required' }}
                            errors={errors}
                        /> */}
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="No. of Lines"
                            name="numberOfLines"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Pipe/Line Size"
                            name="pipeLineSize"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="pipeLineSizeTypeId"
                            label=""
                            options={getOptions('Length')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Not Exceeding"
                            name="pipeLineNotExceeding"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="notExceedingTypeId"
                            label=""
                            options={getOptions('Length')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Consideration"
                            name="consideration"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="considerationTypeId"
                            label=""
                            options={getOptions('Fee')}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Damages"
                            name="damages"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="damagesTypeId"
                            label=""
                            options={getOptions('Fee')}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Ord of Pay Term"
                            name="payTermOrder"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFSelect
                            name="recordTypeId"
                            label="Record Type"
                            options={[ 
                                { value: 1, label: 'Book/Volume' },
                                { value: 2, label: 'Instrument #' }    
                            ]}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Book"
                            name="book"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Page"
                            name="page"
                            register={register}
                            type="number"
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Recording Date"
                            name="recordDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                    </div>

                    <RHFLegend title="Original Legal Description" />
                    <div className="form-row">
                        <div className="form-field" style={{ width: '100%' }}>
                            <textarea
                                name="legalDescription"
                                ref={register}
                                style={{
                                    width: '100%',
                                    height: '6rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                            />
                        </div>
                    </div>

                    <RHFLegend title="Notes" />
                    <div className="form-row" style={{ width: '100%' }}>
                        <div className="form-field" style={{ width: '100%' }}>
                            <textarea
                                name="rightOfWayHolds[0].notes"
                                ref={register}
                                style={{
                                    width: '100%',
                                    height: '6rem',
                                    border: '0',
                                    borderBottom: '1px solid black',
                                    resize: 'none',
                                    overflowY: 'auto',
                                }}
                            />
                        </div>
                    </div>

                    {/* TODO: Set this up for multiple Row-Holds. */}
                    <RHFLegend title="RowHold" />
                    <div hidden={true}>
                        <RHFInputBox
                            label=""
                            name="rightOfWayHolds[0].id"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                        <RHFInputBox
                            label=""
                            name="rightOfWayHolds[0].rightOfWayId"
                            register={register}
                            type="number"
                            disabled={true}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Primary Term"
                            name="rightOfWayHolds[0].primaryTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={handleExpirationChange}
                        />
                        <RHFSelect
                            name="rightOfWayHolds[0].primaryTermTypeId"
                            label="Term Unit"
                            options={getOptions('Time')}
                            onChange={handleExpirationChange}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Eff. Date"
                            name="rightOfWayHolds[0].effectiveDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox 
                            label="Exp. Date" 
                            name="rightOfWayHolds[0].expirationDate" 
                            register={register} 
                            type="date" 
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Extension Term"
                            name="rightOfWayHolds[0].extensionTerm"
                            register={register}
                            type="number"
                            errors={errors}
                            onChange={handleExtensionExpirationChange}
                        />
                        <RHFSelect
                            name="rightOfWayHolds[0].extensionTermTypeId"
                            label=""
                            options={getOptions('Time')}
                            onChange={handleExtensionExpirationChange}
                            register={register}
                            errors={errors}
                        />
                        <RHFInputBox label="Ext. Exp. Date" name="rightOfWayHolds[0].extensionExpirationDate" register={register} type="date" errors={errors} />
                        <RHFInputBox
                            label="Royalty"
                            name="rightOfWayHolds[0].royalty"
                            register={register}
                            type="text"
                            errors={errors}
                        />
                        <RHFSelect
                            name="rightOfWayHolds[0].royaltyTypeId"
                            label=""
                            options={getOptions('Fee')}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div className="form-row">
                        <RHFInputBox
                            label="Void Date"
                            name="rightOfWayHolds[0].voidDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox
                            label="Commit Date"
                            name="rightOfWayHolds[0].commitDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFInputBox
                            label="In Hand Date"
                            name="rightOfWayHolds[0].inHandDate"
                            register={register}
                            type="date"
                            errors={errors}
                        />
                        <RHFSelect
                            name="rightOfWayHolds[0].agentId"
                            label="Agent"
                            options={[{ value: 1, label: 'Ashley Voigt - 137'}]}
                            register={register}
                            errors={errors}
                        />
                    </div>

                    <div>
                        <Button text="Save" type="submit" intent="primary" />
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RightOfWayInfo;


// WEBPACK FOOTER //
// ./src/components/project/sections/TractComponents/RightOfWayInfo.tsx